import moment from "moment";
import { round } from "../../util/util";

export const DATE_FORMAT = "YYYY-MM-DD";
export const MONTH_FORMAT = "YYYY-MM";

/**
 * Days shown as columns, computed once: every cell then reads ready-made values instead of using moment.
 */
export const buildDays = (minDate, nbDays, today) => {
  const todayKey = today.format(DATE_FORMAT);

  return Array.from({length: nbDays}, (_, i) => {
    const day = minDate.clone().add(i, "day");
    const key = day.format(DATE_FORMAT);

    return {
      key,
      month: day.format(MONTH_FORMAT),
      dayOfMonth: day.format("DD"),
      weekday: day.format("ddd"),
      isWeekend: day.isoWeekday() >= 6,
      isToday: key === todayKey
    };
  });
};

/**
 * Month bands of the header: first column index, number of days and label.
 */
export const buildMonths = days => {
  const months = [];

  days.forEach((day, index) => {
    const last = months[months.length - 1];

    if (last && last.month === day.month) {
      last.nbDays++;
    } else {
      months.push({
        month: day.month,
        startIndex: index,
        nbDays: 1,
        label: moment(day.key, DATE_FORMAT).format("MMMM YYYY")
      });
    }
  });

  return months;
};

/**
 * Months to load for the visible columns, with one month before and after to scroll without waiting.
 */
export const monthsAround = (days, firstIndex, lastIndex) => {
  if (days.length === 0) {
    return [];
  }

  const first = moment(days[Math.max(firstIndex, 0)].key, DATE_FORMAT).subtract(1, "month").startOf("month");
  const last = moment(days[Math.min(lastIndex, days.length - 1)].key, DATE_FORMAT).add(1, "month").startOf("month");

  const months = [];

  for (const month = first.clone(); month.isSameOrBefore(last); month.add(1, "month")) {
    months.push(month.format(MONTH_FORMAT));
  }

  return months;
};

export const monthRange = month => {
  const start = moment(month, MONTH_FORMAT).startOf("month");

  return {
    from: start.format(DATE_FORMAT),
    to: start.clone().endOf("month").format(DATE_FORMAT)
  };
};

/**
 * Hours of a worker on a prestation. The API leaves out hours that are not filled in: they count as 0.
 */
export const workerHours = worker => worker.hours || 0;

/**
 * Adds a prestation's worker to the workers of a day ("workerId" => {hours, alert}).
 */
export const addWorker = (workers, worker) => {
  const current = workers.get(worker.workerId) || {hours: 0, alert: false};

  workers.set(worker.workerId, {
    hours: current.hours + workerHours(worker),
    alert: current.alert || Boolean(worker.alert)
  });
};

/**
 * The workers of a day to show: those with hours or an alert. Alerts first, so that a cut-off cell still shows them,
 * then by name.
 */
export const shownWorkers = (workers, workersById) => [...workers.entries()]
  .filter(([, worker]) => worker.hours > 0 || worker.alert)
  .map(([workerId, worker]) => ({...worker, workerId, name: workersById.get(workerId)?.name || "?"}))
  .sort((a, b) => (b.alert - a.alert) || a.name.localeCompare(b.name));

/**
 * Whether the editor's HTML has any text (an empty editor still gives an empty paragraph).
 */
export const hasText = html => Boolean(html)
  && new DOMParser().parseFromString(html, "text/html").body.textContent.trim() !== "";

/**
 * What a site's day shows, from its prestations of that day: the workers with hours or an alert,
 * and the prestations' infos (description) and report (progressReport) when they have text.
 */
export const siteDayDetails = (prestations, workersById) => {
  const workers = new Map();
  const notes = [];

  prestations.forEach(prestation => {
    prestation.workers.forEach(worker => addWorker(workers, worker));

    const note = {
      id: prestation.id,
      description: hasText(prestation.description) ? prestation.description : null,
      progressReport: hasText(prestation.progressReport) ? prestation.progressReport : null
    };

    if (note.description || note.progressReport) {
      notes.push(note);
    }
  });

  return {workers: shownWorkers(workers, workersById), notes};
};

/**
 * Looks up what a cell needs in one step, instead of searching all the events and prestations for every cell.
 * Keys: "siteId|date" for a site's day (events, hours, workers), "date" for the whole day.
 */
export const buildIndex = (events, prestations) => {
  const siteDays = new Map();
  const customEventDays = new Set();
  const customEvents = new Map();
  const totalHours = new Map();

  const siteDay = key => {
    if (!siteDays.has(key)) {
      siteDays.set(key, {hasEvents: false, events: [], hours: 0, workers: new Map()});
    }

    return siteDays.get(key);
  };

  events.forEach(event => {
    if (event.siteId) {
      const day = siteDay(event.siteId + "|" + event.date);

      day.hasEvents = true;
      day.events.push(event);
    } else {
      customEventDays.add(event.date);
      customEvents.set(event.date, [...(customEvents.get(event.date) || []), event]);
    }
  });

  prestations.forEach(prestation => {
    const hours = prestation.workers.reduce((acc, worker) => acc + workerHours(worker), 0);
    const day = siteDay(prestation.siteId + "|" + prestation.date);

    day.hours += hours;
    prestation.workers.forEach(worker => addWorker(day.workers, worker));
    totalHours.set(prestation.date, (totalHours.get(prestation.date) || 0) + hours);
  });

  siteDays.forEach(value => {
    value.hours = round(value.hours, 1);
  });

  totalHours.forEach((value, key) => {
    totalHours.set(key, round(value, 1));
  });

  return {siteDays, customEventDays, customEvents, totalHours};
};

export const addDays = (dayKey, nbDays) => moment(dayKey, DATE_FORMAT).add(nbDays, "day").format(DATE_FORMAT);

export const startOfWeek = dayKey => moment(dayKey, DATE_FORMAT).startOf("week").format(DATE_FORMAT);

export const daysBetween = (fromKey, toKey) => moment(toKey, DATE_FORMAT).diff(moment(fromKey, DATE_FORMAT), "days");

/**
 * Title of a week: "novembre 2026", "oct. - nov. 2026" or "déc. 2026 - janv. 2027".
 */
export const weekTitle = (firstDayKey, lastDayKey) => {
  const first = moment(firstDayKey, DATE_FORMAT);
  const last = moment(lastDayKey, DATE_FORMAT);

  if (first.format(MONTH_FORMAT) === last.format(MONTH_FORMAT)) {
    return first.format("MMMM YYYY");
  }

  if (first.year() === last.year()) {
    return first.format("MMM") + " - " + last.format("MMM YYYY");
  }

  return first.format("MMM YYYY") + " - " + last.format("MMM YYYY");
};
